import React, { useState, useEffect, useRef } from 'react';
import { MapPinIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

const LocationSearch = ({ 
  value, 
  onChange, 
  placeholder = "Search for a location...", 
  onSelect,
  className = "" 
}) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [requestingLocation, setRequestingLocation] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Get user's current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setRequestingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setRequestingLocation(false);
        // If there's a query, search again with location bias
        if (query.trim()) {
          searchLocations(query, position.coords.latitude, position.coords.longitude);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationError('Unable to get your location');
        setRequestingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  };

  // Search locations using the API
  const searchLocations = async (searchQuery, lat = null, lng = null) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({ query: searchQuery });
      if (lat && lng) {
        params.append('lat', lat);
        params.append('lng', lng);
      }

      const response = await fetch(`/api/locations/search?${params.toString()}`);
      const data = await response.json();

      if (response.ok && data.places) {
        setSuggestions(data.places);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(true); // Show "no results" message
      }
    } catch (error) {
      console.error('Location search error:', error);
      setSuggestions([]);
      setShowSuggestions(true);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchLocations(query, userLocation?.lat, userLocation?.lng);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, userLocation]);

  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    onChange(newQuery);
    setSelectedIndex(-1);
  };

  const handleSuggestionClick = (location) => {
    const displayName = location.name || location.address || query;
    setQuery(displayName);
    onChange(displayName);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    if (onSelect) {
      onSelect({
        ...location,
        displayName: displayName
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const clearInput = () => {
    setQuery('');
    onChange('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim() && setShowSuggestions(true)}
          placeholder={placeholder}
          className="block w-full pl-10 pr-20 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-1">
          {query && (
            <button
              type="button"
              onClick={clearInput}
              className="text-gray-400 hover:text-gray-600 mr-1"
              title="Clear"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={requestingLocation}
            className={`text-gray-400 hover:text-blue-600 mr-1 ${requestingLocation ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Use my location"
          >
            {requestingLocation ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            ) : (
              <MapPinIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Location error message */}
      {locationError && (
        <div className="mt-1 text-xs text-red-600">{locationError}</div>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 p-3 z-50">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
            <span className="ml-2 text-sm text-gray-600">Searching locations...</span>
          </div>
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && !isLoading && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto z-50"
        >
          {suggestions.map((location, index) => (
            <button
              key={location.id || index}
              type="button"
              onClick={() => handleSuggestionClick(location)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                index === selectedIndex ? 'bg-primary-50' : ''
              } ${index === 0 ? 'rounded-t-lg' : ''} ${
                index === suggestions.length - 1 ? 'rounded-b-lg' : ''
              }`}
            >
              <div className="flex items-start space-x-3">
                <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {location.name || location.address}
                    </p>
                    {location.type && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                        {location.type}
                      </span>
                    )}
                  </div>
                  {location.address && location.name && location.address !== location.name && (
                    <p className="text-sm text-gray-600 truncate mt-1">
                      {location.address}
                    </p>
                  )}
                  {location.coordinates && (
                    <p className="text-xs text-gray-400 mt-1">
                      {location.coordinates.lat?.toFixed(4)}, {location.coordinates.lng?.toFixed(4)}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && suggestions.length === 0 && !isLoading && query.trim() && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 p-4 z-50">
          <div className="text-center">
            <MapPinIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No locations found</p>
            <p className="text-xs text-gray-400 mt-1">
              Try searching for parks, fields, venues, or addresses
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
